import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  OnDestroy,
  inject
} from '@angular/core';
import { TLang } from '../form-fileds.model';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { UtilService } from 'src/app/shared/services';
import { FormGroup, FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FUNDRAISER_LABELS } from '../translations/stepper_label';

interface ProgressInfo {
  totalFields: number;
  completedFields: number;
  remainingFields: number;
  progressPercentage: number;
  isComplete: boolean;
  visibleFieldKeys: string[];
  completedFieldKeys: string[];
  remainingFieldKeys: string[];
}

@Component({
  selector: 'app-stepper',
  standalone: true,
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  imports: [CommonModule, MatIconModule, FormsModule],
})
export class StepperComponent implements OnInit, OnChanges, OnDestroy {
  public isMobile: boolean = false;
  public title: string = "";

  public readonly util = inject(UtilService);

  @Input() formData: { [key: string]: any } = {};
  @Input() formGroup!: FormGroup;
  @Input() causeName: string = '';
  @Input() isFinalStep: boolean = false;
  @Input() selectedLang: TLang = this.util.storage.get('safLanguage') ?? 'en';
  @Input() supportedLang: TLang;

  @Output() setLanguage = new EventEmitter<TLang>();
  @Output() triggerGoBack = new EventEmitter<any>();

  @ViewChild('langSelect') langSelectRef!: ElementRef<HTMLSelectElement>;

  formChangeSub?: Subscription;

  overallProgress: ProgressInfo = {
    totalFields: 0,
    completedFields: 0,
    remainingFields: 0,
    progressPercentage: 0,
    isComplete: false,
    visibleFieldKeys: [],
    completedFieldKeys: [],
    remainingFieldKeys: [],
  };

  ngOnInit() {
    console.log(this.selectedLang)
    this.isMobile = this.util.isMobile();
    this.updateProgress();

    if (this.formGroup) {
      this.formChangeSub = this.formGroup.valueChanges.subscribe(() => {
        this.updateProgress();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.formGroup || changes.formData || changes.selectedLang || changes.causeName) {
      this.updateProgress();

      const { raisingFor, name, relation, beneficiaryName } = this.formData;

      this.title = this.getFundraiserTitle({
        raisingFor,
        name,
        relation,
        beneficiaryName,
        cause: this.causeName,
        lang: this.selectedLang,
      });
    }
  }

  ngOnDestroy() {
    this.formChangeSub?.unsubscribe();
  }

  updateProgress() {
    const progress = this.calculateProgress();
    this.overallProgress = {
      ...progress,
      progressPercentage: this.getDisplayProgress(progress.progressPercentage),
    };
  }

  getDisplayProgress(actual: number): number {
    if (!this.isFinalStep && actual > 85) return 85;
    if (actual < 50) return 49;
    return actual;
  }

  calculateProgress(): ProgressInfo {
    if (!this.formGroup) return this.overallProgress;

    const controls = this.formGroup.controls;

    const visibleFieldKeys = Object.keys(controls).filter(
      key => this.formGroup.get(key)?.enabled
    );

    const completedFieldKeys = visibleFieldKeys.filter(key => {
      const control = this.formGroup.get(key);
      return control?.valid;
    });

    const totalFields = visibleFieldKeys.length;
    const completedFields = completedFieldKeys.length;

    return {
      totalFields,
      completedFields,
      remainingFields: totalFields - completedFields,
      progressPercentage:
        totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0,
      isComplete: completedFields === totalFields,
      visibleFieldKeys,
      completedFieldKeys,
      remainingFieldKeys: visibleFieldKeys.filter(k => !completedFieldKeys.includes(k)),
    };
  }

  triggerSelect(): void {
    this.langSelectRef?.nativeElement?.click();
  }

  onLanguageChange(value: TLang) {
    this.setLanguage.emit(value);
  }

  goBack() {
    this.triggerGoBack.emit();
  }

  getFundraiserTitle(data: {
    raisingFor: string;
    name: string;
    relation?: string;
    beneficiaryName?: string;
    cause?: string;
    lang: 'en' | 'hi' | 'ta' | 'te';
  }): string {
    const { raisingFor, name, relation, beneficiaryName, cause, lang } = data;
    const labels = FUNDRAISER_LABELS[lang] || FUNDRAISER_LABELS['en'];

    if (raisingFor === 'ngo') {
      return labels.ngo;
    }

    if (raisingFor === 'someoneElse') {
      if (beneficiaryName) {
        return labels.someoneElseWithName
          .replace('{{name}}', beneficiaryName)
          .replace('{{cause}}', cause || '');
      } else {
        const localizedRelation = labels.relations?.[relation ?? 'Other'] || '';
        if(!relation) return labels.someoneElseWithName.replace('{{name}}', name).replace('{{cause}}', cause);;
        return labels.someoneElseWithRelation
          .replace('{{name}}', name)
          .replace('{{relation}}', localizedRelation);
      }
    }

    if (raisingFor === 'self') {
      if (cause) {

        return labels.selfWithCause
          .replace('{{name}}', name)
          .replace('{{cause}}', cause);
      }
      return labels.self.replace('{{name}}', name);
    }

    return labels.default;
  }

}
